/**
 * The records behind "In Vantio vorhanden, in Bexio nicht mehr vorhanden".
 *
 * WHY THIS FINDING IS FETCHED AND THE OTHER THIRTY-FOUR ARE NOT.
 * Every other data-quality finding arrives inside the revenue analytics response,
 * complete, and the drawer pages through it in the DOM. This one deliberately does
 * not: the analytics response carries the COUNT
 * (`current_receivables.excluded.provider_missing`) and nothing else. Six invoices
 * are marked provider-missing in production today, and there is no upper bound on
 * that number — a full inventory that stops finding a year of invoices would put
 * every one of them into a payload the reader never asked for. So the count travels
 * with the figures it explains, and the rows are read separately, paginated by the
 * server, only once somebody opens the finding.
 *
 * THE READER IS THE PERSISTENT STATE, NOT AN AGGREGATE.
 * `GET /admin/bexio/invoices?provider_presence=missing` filters on
 * `provider_missing_at IS NOT NULL` in SQL, which is the same column the analytics
 * count is derived from. Period attribution publishes its own provider-missing
 * numbers (`attributed_provider_missing`, `provider_missing_invoices`) and they are
 * NOT usable here: they exist to keep the attribution partition summing correctly
 * over invoices a draft-ledger row points at, and the six real production cases had
 * `draft_requests = 0`. A drilldown built on them would have shown an empty list for
 * the only case this feature exists for.
 *
 * NO CACHE, ON PURPOSE.
 * Closing the drawer discards everything. When a later inventory finds an invoice
 * again, PM-2 sets `provider_missing_at` back to NULL, the analytics count drops and
 * this reader stops returning the row — with no state of its own to contradict that.
 * A cache would be a second, staler answer to a question the backend already
 * answers, and the row it held would outlive the fact.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { fetchBexioInvoices } from "../../../api/adminData/bexio";

/** The server-side filter value. The backend rejects anything else with a 422. */
export const PROVIDER_PRESENCE_MISSING = "missing";

/**
 * Rows per request.
 *
 * The same twenty the drilldown drawer already reveals per click, so the two paths
 * through that drawer feel identical — the difference between them is where the
 * next twenty come from, and that is not something a reader should have to notice.
 * A guard pins this to `RevenueQualityDetailDrawer`'s `PAGE` rather than importing
 * it, so the number cannot drift while the two files stay independent.
 */
export const PROVIDER_MISSING_PAGE_SIZE = 20;

/** Group and finding key, so the config and the wiring cannot name it differently. */
export const PROVIDER_MISSING_GROUP_ID = "receivables";
export const PROVIDER_MISSING_KEY = "provider_missing";
export const PROVIDER_MISSING_DETAIL_KEY = `${PROVIDER_MISSING_GROUP_ID}.${PROVIDER_MISSING_KEY}`;

/** Shown when the request fails and the backend gave no message of its own. */
export const PROVIDER_MISSING_ERROR =
  "Die betroffenen Rechnungen konnten nicht geladen werden.";

const EMPTY = { rows: [], total: null, loading: false, error: null };

/**
 * Load provider-missing invoices while `open` is true.
 *
 * Returns the shape `RevenueQualityDetailDrawer` renders for a server-backed
 * finding: the accumulated rows, the server's own total, and the three states a
 * request has. `hasMore` compares the two counts rather than trusting a page to be
 * full — a final page of exactly twenty would otherwise look like there is more.
 */
export function useProviderMissingInvoices(open) {
  const [state, setState] = useState(EMPTY);
  // Monotonic request id. Every response checks it before writing, so a response
  // that arrives after the drawer closed (or after a reload replaced it) is
  // discarded instead of resurrecting a list the reader has already dismissed.
  const request = useRef(0);
  // Set SYNCHRONOUSLY, unlike the `loading` flag in state: two clicks in one tick
  // would both read the pre-render `loading: false` and request the same offset
  // twice, which is how a paginated list grows duplicates.
  const inFlight = useRef(false);
  // The latest rows/total, readable from an event handler without making `loadMore`
  // depend on them — the handler must not be re-created on every page that lands.
  const latest = useRef(EMPTY);
  latest.current = state;

  const load = useCallback((offset) => {
    request.current += 1;
    const id = request.current;
    inFlight.current = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    fetchBexioInvoices({
      provider_presence: PROVIDER_PRESENCE_MISSING,
      limit: PROVIDER_MISSING_PAGE_SIZE,
      offset,
    })
      .then((res) => {
        if (id !== request.current) return;
        inFlight.current = false;
        const items = Array.isArray(res?.items) ? res.items : [];
        setState((s) => ({
          // Offset 0 REPLACES rather than appends: it is the first page of a fresh
          // open or of a retry, and appending it would duplicate every row.
          rows: offset === 0 ? items : [...s.rows, ...items],
          // The server's count of the whole filtered set, never the rows in hand.
          total: Number.isFinite(Number(res?.total)) ? Number(res.total) : items.length,
          loading: false,
          error: null,
        }));
      })
      .catch((err) => {
        if (id !== request.current) return;
        inFlight.current = false;
        // The rows already shown stay: a failed second page does not make the first
        // one untrue, and clearing it would look like the invoices had disappeared.
        setState((s) => ({
          ...s,
          loading: false,
          error: err?.message || PROVIDER_MISSING_ERROR,
        }));
      });
  }, []);

  useEffect(() => {
    if (!open) {
      // Invalidate anything in flight, then forget. See "NO CACHE" above.
      request.current += 1;
      inFlight.current = false;
      setState(EMPTY);
      return;
    }
    load(0);
  }, [open, load]);

  const hasMore = state.total != null && state.rows.length < state.total;

  const loadMore = useCallback(() => {
    const s = latest.current;
    if (inFlight.current || s.total == null || s.rows.length >= s.total) return;
    load(s.rows.length);
  }, [load]);

  const reload = useCallback(() => {
    if (inFlight.current) return;
    load(0);
  }, [load]);

  return {
    rows: state.rows,
    total: state.total,
    loading: state.loading,
    error: state.error,
    hasMore,
    loadMore,
    reload,
  };
}
